import { webApiClient } from '../authentication';

interface ProductEntry {
  sku?: string | null;
  priceInCents?: number | null;
  activationFeeInCents?: number | null;
  pricingInCents?: Record<string, number> | null;
}

interface ShippingOptionEntry {
  code?: string | null;
  pricingInCents?: Record<string, number> | null;
  priceInCents?: number | null;
  isExpedited?: boolean | null;
  exchangeOnly?: boolean | null;
  inPersonEventOnly?: boolean | null;
}

interface ProductsResponse {
  products: ProductEntry[];
  shippingOptions?: ShippingOptionEntry[];
}

export interface PriceLine {
  label: string;
  amountInCents: number;
  /** Rendered as a deduction rather than a charge. */
  isCredit?: boolean;
  /** Rendered as "Included" rather than an amount. */
  isIncluded?: boolean;
  /**
   * Rendered as "Free" in the credit colour rather than as "$0".
   *
   * A $0 line and a missing line look identical to a shopper who is trying to work out whether
   * shipping is about to be added, so this states the zero instead of omitting it.
   */
  isFree?: boolean;
}

export interface PriceBreakdown {
  lines: PriceLine[];
  totalInCents: number;
  /**
   * The shipping method the order is placed with. Rides along here because it comes from the same
   * products response, and because the purchase is rejected as unfulfillable without one -- this flow
   * has no shipping selector, so something has to choose.
   */
  shippingCode: string | null;
}

/**
 * The credit line's label when the chip carries no rescue attribution.
 *
 * It is a *registration* credit by default and deliberately not an adoption one: roughly a quarter of
 * this traffic arrives via a breeder, a vet or no reseller at all, and adoption-worded money is wrong
 * for them. What is true of everyone on the screen is that they just registered a Fi Nano chip.
 */
const DEFAULT_CREDIT_LABEL = 'Fi Nano registration credit';

/**
 * Builds the bag's itemised breakdown from the plan's own server-side pricing.
 *
 * Both figures come from the plan record: `pricingInCents.USD` is the membership price and
 * `activationFeeInCents` is what is actually charged today. Nothing is hardcoded, so the screen cannot
 * drift from the catalogue.
 *
 * The credit equals the full membership price because that is definitionally what this offer is -- six
 * months free, backed by a fixed-amount coupon worth the whole 6-month term. If the offer ever becomes
 * a partial discount, this has to start reading the coupon's own amount instead of inferring it.
 *
 * Deliberately NOT sourced from `/api/promotions/code-details`. That endpoint exists to power per-plan
 * "save $X" badges and is the wrong shape for this: `getCodeDetails` looks the code up directly and
 * never runs `resolvePromoAlias`, so a GWA alias can never resolve there — alias resolution needs the
 * signed entitlement the cart carries, which a GET has no access to. It also returns empty arrays
 * whenever its own subscription-plan fetch comes back empty, which was observed against a local
 * backend during a Recurly subscription-API incident; the plan catalogue used here kept working
 * throughout, so this path also survives that class of outage.
 */
export function buildPriceBreakdown(
  products: ProductEntry[],
  planSku: string,
  shippingOptions: ShippingOptionEntry[] = [],
  creditLabel: string = DEFAULT_CREDIT_LABEL,
): PriceBreakdown | null {
  const plan = products.find((product) => product.sku === planSku);
  const membershipInCents = plan?.pricingInCents?.USD ?? plan?.priceInCents ?? 0;
  if (!plan || membershipInCents <= 0) {
    return null;
  }

  const activationInCents = plan.activationFeeInCents ?? 0;

  const lines: PriceLine[] = [
    { label: 'Fi Mini GPS tracker', amountInCents: 0, isIncluded: true },
    { label: '6 months of membership', amountInCents: membershipInCents },
    { label: creditLabel, amountInCents: membershipInCents, isCredit: true },
  ];
  if (activationInCents > 0) {
    lines.push({ label: 'Activation fee', amountInCents: activationInCents });
  }

  // Priced from the same option the order is placed with, never hardcoded to zero. Shipping is free
  // on this offer today, but a catalogue change that made it cost something would otherwise leave the
  // lines silently failing to sum to the total.
  const shipping = cheapestOrdinaryShipping(shippingOptions);
  // A method the catalogue gives no price for is not a free one, so it gets no line at all rather
  // than being quoted at $0.
  if (shipping && shipping.priceInCents !== null) {
    lines.push({
      label: 'Shipping',
      amountInCents: shipping.priceInCents,
      isFree: shipping.priceInCents === 0,
    });
  }

  return {
    lines,
    totalInCents: activationInCents + (shipping?.priceInCents ?? 0),
    shippingCode: shipping?.code ?? null,
  };
}

/** Fetches the catalogue and builds the breakdown. Null when the offer's plan is absent from it. */
export async function fetchPriceBreakdown(planSku: string, creditLabel?: string): Promise<PriceBreakdown | null> {
  try {
    const { data } = await webApiClient.get<ProductsResponse>('/api/ecommerce/products');
    return buildPriceBreakdown(data.products ?? [], planSku, data.shippingOptions ?? [], creditLabel);
  } catch {
    return null;
  }
}

interface EligibleShipping {
  code: string;
  /** Null when the catalogue carries no price for it, which must never be read as free. */
  priceInCents: number | null;
}

/**
 * The cheapest ordinary shipping method, with its price.
 *
 * Chosen rather than hardcoded so a renamed or repriced option follows the catalogue. Expedited,
 * exchange-only and in-person-event options are excluded: the first two are not what this offer sells,
 * and `so-in_person` is $0 but only valid at an event, so picking on price alone would select it.
 *
 * Returns the option rather than just its code so the summary can price the line from the same choice
 * the order is placed with -- the two cannot disagree if they come from one call.
 */
function cheapestOrdinaryShipping(options: ShippingOptionEntry[]): EligibleShipping | null {
  const eligible = options
    .filter((option) => !!option.code && !option.isExpedited && !option.exchangeOnly && !option.inPersonEventOnly)
    .map((option) => ({
      code: option.code as string,
      priceInCents: option.pricingInCents?.USD ?? option.priceInCents ?? null,
    }))
    // an unpriced option sorts last rather than sorting as free, so it is only ever a last resort
    .sort((a, b) => (a.priceInCents ?? Number.MAX_SAFE_INTEGER) - (b.priceInCents ?? Number.MAX_SAFE_INTEGER));

  return eligible[0] ?? null;
}

/** Cents to a plain dollar string. Whole dollars drop the decimals, matching the prototype. */
export function formatCents(amountInCents: number): string {
  const dollars = amountInCents / 100;
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}
